import type { Authenticator, AuthenticatorOptions } from '@ms/yammer-web-authenticators';
import {
  AcquireTokenAuthError,
  AdalRedirectAuthenticator,
  AuthenticatorType,
  setAuthenticator,
} from '@ms/yammer-web-authenticators';
import { TeamsMeetingAuthenticator } from '@ms/yammer-web-authenticators/dist/teamsmeeting';
import { getMetaOsHubContextThrows, hasMetaOsHubContext } from '@ms/yammer-web-support/dist/metaOsHub';
import config from 'bootstrap/config';
import { GetMeetingDetailsTimedoutError } from 'domains/teams/types';
import { isAnonymousUser } from 'teams';
import { resolveMeetingTenantId } from 'teams/meetingDetails';
import { getNetworkIdFromQueryParam } from 'teams/queryParams';

import { getAuthTokenCache, initializeAuthTokenCache } from './authTokenCache';
import { isNAASupportAvailable } from './isNAASupportAvailable';
import { reportAuthError } from './reportAuthError';

type IsEngageEvent = () => boolean;
const isEngageEvent: IsEngageEvent = () => !!getNetworkIdFromQueryParam(window.location.href);

type GetAuthenticator = () => Promise<Authenticator>;
const getAuthenticator: GetAuthenticator = async () => {
  if (!(await hasMetaOsHubContext())) {
    return new AdalRedirectAuthenticator(config.adal);
  }

  initializeAuthTokenCache();
  const isAnonymous = await isAnonymousUser();

  if (await isNAASupportAvailable()) {
    const shouldAutoRefresh = false;
    const context = await getMetaOsHubContextThrows();
    const tenant = config.adal.tenant;
    const teamsResource = config.adal.resources.yammer;
    const tenantId = context.user?.tenant?.id;

    let meetingTenantId: string | undefined;

    if (isEngageEvent()) {
      try {
        meetingTenantId = await resolveMeetingTenantId(window.location.href);
      } catch (error) {
        const errorCode =
          error instanceof GetMeetingDetailsTimedoutError
            ? 'GuestMeetingTenantResolutionTimedout'
            : 'GuestMeetingTenantResolutionFailed';
        const authError = new AcquireTokenAuthError({
          errorCode,
          errorMessage: (error as Error).message,
        });

        await reportAuthError(authError, { authenticatorType: AuthenticatorType.TeamsMeetingV2 }, { tenantId });
        throw authError;
      }
    }

    // oxlint-disable-next-line typescript/no-explicit-any
    (config as any).adal = config.msal;

    const reportAuthErrorWithContext: NonNullable<AuthenticatorOptions['onError']> = (error, properties) =>
      reportAuthError(error, properties, { tenantId, meetingTenantId });

    const authenticatorOptions = {
      getAuthTokenCache,
      instance: config.adal.instance,
      clientId: config.adal.clientId,
      tenant,
      teamsResource: isEngageEvent() ? teamsResource : undefined,
      meetingTenantId,
      onError: reportAuthErrorWithContext,
      accountFilter: {
        homeAccountId: context.user?.id,
        tenantId,
        loginHint: context.user?.loginHint,
      },
      shouldAutoRefresh,
    };

    const { TeamsMeetingAuthenticatorV2 } = await import(
      /* webpackChunkName: "4-auth-naa" */ '@ms/yammer-web-authenticators/dist/teamsmeeting'
    );

    return new TeamsMeetingAuthenticatorV2(authenticatorOptions, isAnonymous);
  }

  return new TeamsMeetingAuthenticator(getAuthTokenCache, isAnonymous);
};

export type SetupAuthenticator = () => Promise<Authenticator>;
export const setupAuthenticator: SetupAuthenticator = async () => {
  const authenticator = await getAuthenticator();
  setAuthenticator(authenticator);

  return authenticator;
};

export default setupAuthenticator;
